// @flow
import React, { useState } from 'react';
import Image from 'components/base/Image';
import type { PostItem } from 'store/modules/listing';
import { resizeImage, numberWithCommas } from 'lib/common';
import { Link } from 'react-router-dom';
import { track, TRACKING_EVENTS } from 'lib/tracking';
import './SearchResultItem.scss';

type Props = {
  post: PostItem,
  menu: string,
  index?: number,
  searchTerm?: string,
};

const SearchResultItem = ({ post, menu, index, searchTerm }: Props) => {
  const {
    id,
    title,
    username,
    thumbnail,
    price,
    brand,
    url_slug,
    _formatted,
    part_numbers,
    cas_numbers,
    badges,
  } = post;

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageAspectRatio, setImageAspectRatio] = useState(null);

  const handleImageLoad = e => {
    const img = e.target;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    setImageAspectRatio(aspectRatio);
    setImageLoaded(true);
  };

  const getImageClass = () => {
    if (!imageLoaded || !imageAspectRatio) return 'loading';
    if (imageAspectRatio > 1.5) {
      return 'landscape'; // 가로가 긴 이미지
    }
    if (imageAspectRatio < 0.7) {
      return 'portrait'; // 세로가 긴 이미지
    }
    return 'square'; // 정사각형에 가까운 이미지
  };

  const nextLink =
    menu === 'posts' ? `/@${username}/${url_slug}` : `/@${username}/${menu}/${url_slug}`;

  const handleClick = e => {
    // 상품 선택 추적
    track(TRACKING_EVENTS.PRODUCT_SELECTED, {
      productId: id,
      productSlug: url_slug,
      productTitle: title,
      brand: brand || '',
      listId: searchTerm ? `search:${searchTerm}` : 'search',
      listName: searchTerm ? `검색: ${searchTerm}` : '검색 결과',
      index: index || 0,
    });

    // Ctrl/Cmd + 클릭 시 새 탭에서 열기
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      window.open(nextLink, '_blank');
    }
  };

  return (
    <div className="SearchResultItem">
      <Link
        to={nextLink}
        className="product-card-link"
        onClick={handleClick}
      >
        <div className="product-card">
          <div className="product-image">
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={title}
                loading="lazy"
                className={getImageClass()}
                onLoad={handleImageLoad}
              />
            ) : (
              <div className="no-image">
                <span role="img" aria-label="상품 이미지 없음">
                  📦
                </span>
              </div>
            )}
            <div className="product-overlay">
              <span className="view-product">상품 보기</span>
            </div>
          </div>

          <div className="product-content">
            <div className="product-header">
              {brand && <span className="brand-text">{brand}</span>}
            </div>

            <div className="product-title">
              {_formatted && _formatted.title ? (
                <h3 dangerouslySetInnerHTML={{ __html: _formatted.title }}></h3>
              ) : (
                <h3>{title}</h3>
              )}
            </div>

            {price && price !== '0' && price !== 0 ? (
              <div className="product-price">
                <span className="price-amount">{numberWithCommas(price)}</span>
                <span className="price-unit">원</span>
              </div>
            ) : (
              <div className="product-price-inquiry">
                <span>가격 문의</span>
              </div>
            )}

            <div className="product-meta">
              {(_formatted && _formatted.part_numbers) || part_numbers
                ? (() => {
                    const partNumberText = (_formatted && _formatted.part_numbers) || part_numbers;
                    // 배열인 경우와 문자열인 경우 모두 처리
                    const partNumberArray = Array.isArray(partNumberText)
                      ? partNumberText
                      : partNumberText.split(',').map(p => p.trim());

                    // <mark> 태그가 있는 항목을 우선 정렬
                    const sortedPartNumbers = partNumberArray.sort((a, b) => {
                      const aHasMark = a.includes('<mark>');
                      const bHasMark = b.includes('<mark>');
                      if (aHasMark && !bHasMark) return -1;
                      if (!aHasMark && bHasMark) return 1;
                      return 0;
                    });

                    const partNumbers = sortedPartNumbers.slice(0, 1);
                    const remainingCount = partNumberArray.length - 1;

                    return (
                      <div className="meta-item">
                        <div className="part-numbers">
                          <span className="meta-value part-number">
                            <span dangerouslySetInnerHTML={{ __html: partNumbers[0] }} />
                            {remainingCount > 0 && (
                              <span className="part-number-count"> 외 {remainingCount}개</span>
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  })()
                : null}

              {(_formatted && _formatted.cas_numbers) || cas_numbers ? (
                <div className="meta-item">
                  <span
                    className="meta-value cas-number"
                    dangerouslySetInnerHTML={{
                      __html: (_formatted && _formatted.cas_numbers) || cas_numbers,
                    }}
                    // eslint-disable-next-line react/jsx-closing-tag-location
                  ></span>
                </div>
              ) : null}
            </div>

            <div className="product-badges">
              {badges && badges.length > 0 ? (
                badges.slice(0, 3).map((badge, badgeIndex) => (
                  <span
                    key={`${badge.name}-${badgeIndex}`}
                    className="product-badge"
                    style={{ backgroundColor: badge.color }}
                  >
                    {badge.name}
                  </span>
                ))
              ) : (
                <span className="product-badge-placeholder"></span>
              )}
            </div>

            {_formatted && _formatted.body && (
              <div className="product-description">
                <p dangerouslySetInnerHTML={{ __html: _formatted.body }}></p>
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};

export default SearchResultItem;



// WEBPACK FOOTER //
// ./src/components/search/SearchResultItem/SearchResultItem.js